import { useContext, useEffect, useState } from "react";
import ReportingDropdown from "../../components/Navigation/BreDropdown";
import Expand from "../../components/Navigation/Expand";
import { useIframeContext } from "../../context/IframeContext";
import NoResultsMessage from "../../components/Filters/NoResultsMessage";
import HomeButton from "../../components/Navigation/HomeButton";
import CompaniesContext from "../../context/CompaniesContext";
import CompanyTable from "../../components/Companies/CompanyTable";
import AddCompanyButton from "../../components/Companies/AddCompanyButton";
import { dropdownStyles } from "../../utils/customFormStyles";
import Select from 'react-select';
import MergeCompaniesButton from "../../components/Companies/MergeCompaniesButton";
import NewSearch from "../../components/General/NewSearch";


function Companies() {
    const { companies, loading: companiesLoading } = useContext(CompaniesContext);
    const { isIframe } = useIframeContext();

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [industryFilter, setIndustryFilter] = useState<string>('');
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);

    // Define storage keys for persisting filters
    const FILTER_STORAGE_KEY = 'companiesFilters';

    const industryNames = Array.from(
        new Set(companies.map(c => c.industry?.name).filter((n): n is string => Boolean(n)))
    ).sort();

    const industryOptions = industryNames.map(name => ({
        value: name,
        label: name,
    }));

    const filteredCompanies = companies.filter(company => {
        const matchesSearch = searchTerm === '' || (
            company.legal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            company.trade_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        const matchesIndustry = industryFilter === '' || company.industry?.name === industryFilter;

        return matchesSearch && matchesIndustry;
    });

    // Load filters from sessionStorage when the component mounts
    useEffect(() => {
        setLoading(true);

        const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
        if (savedFilters) {
            const parsed = JSON.parse(savedFilters);
            setSearchTerm(parsed.searchTerm || '');
            setIndustryFilter(parsed.industryFilter || '');
        } else {
            setSearchTerm('');
            setIndustryFilter('');
        }
        setLoading(false);
    }, []);

    const saveFiltersToStorage = (filters: { searchTerm: string; industryFilter: string }) => {
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    };

    const handleSearch = (term: string) => {
        setSearchTerm(term);
        saveFiltersToStorage({ searchTerm: term, industryFilter });
    };

    const handleIndustryChange = (selectedOption: any) => {
        const value = selectedOption ? selectedOption.value : '';
        setIndustryFilter(value);
        saveFiltersToStorage({ searchTerm, industryFilter: value });
    };

    const handleRemoveFilters = () => {
        setLoading(true);
        setSearchTerm('');
        setIndustryFilter('');
        sessionStorage.removeItem(FILTER_STORAGE_KEY);
        setLoading(false);
    };

    return ( 
        <>
            <ReportingDropdown/>
            <div className="px-4 py-0 sm:py-4 md:py-4 lg:py-4">
                <div className="mx-2 my-2 relative">
                    <div className="flex flex-wrap items-end w-full justify-start">
                        <HomeButton/>

                        {isIframe && 
                        <div className="flex sm:mt-0 mt-1 mb-1 absolute md:relative top-0 right-0 md:top-auto md:right-auto md:ml-auto transform md:transform-none">
                            <Expand/>
                        </div>
                        }
                    </div>
                </div>
                <div className="mx-2 my-4 flex flex-wrap relative">
                    <div className="w-5/6 sm:w-auto">
                        <NewSearch onSearch={handleSearch} isLarge={true} value={searchTerm}/>
                    </div>
                    <div className="w-full sm:w-auto sm:ml-4 sm:mt-0 mt-2">
                        <Select
                            id="selectIndustryType"
                            value={industryFilter ? { value: industryFilter, label: industryFilter } : null}
                            onChange={handleIndustryChange}
                            options={industryOptions}
                            placeholder="Select Industry"
                            styles={dropdownStyles()}
                            isClearable
                        />
                    </div>
                    <AddCompanyButton/>
                    <MergeCompaniesButton disabled={selectedRows.length < 2} selectedRows={selectedRows} />
                </div>

                {companiesLoading || loading ? (
                    <div className="flex justify-center items-center my-16">
                            <p className="text-md font-bold text-color-cai-dashboard mr-8">Loading Companies</p>
                            <div className="loader"></div>
                    </div>
                ) : (
                    <div className="mx-2 my-4">
                        {companies.length > 0 ? (
                            <CompanyTable data={filteredCompanies} handleSelectRows={setSelectedRows} />
                        ) : (
                            <NoResultsMessage 
                                itemName="companies"
                                hasFilters={true}
                                onRemoveFilters={handleRemoveFilters}
                            />
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

export default Companies;